/**
 * A multi-series sparkline.
 *
 * The port of `LiveChartBuilder`, which built `Polyline`s with `PointCollection`s in a fixed 300x40
 * space and scaled them with a `Viewbox` - because OpenSilver had no way to draw. Here the control
 * paints itself with `rc.drawPolyline`, so it draws at the real pixel size and the geometry is not a
 * scaled approximation of it.
 *
 * Three rules come straight from the OpenSilver version and are the difference between a chart that
 * tells the truth and one that looks plausible:
 *
 *   * **Downsampled by per-bucket MAXIMUM, never by average.** The 24-hour view exists to catch
 *     spikes; averaging sands them off, which is the one thing the view is for.
 *   * **The X window ends at the newest sample's OWN timestamp** (the agent's clock), so skew
 *     between the browser and the device cannot slide the graph.
 *   * **A fixed `max` where one is natural, autoscale to the window's peak otherwise.** 100 for a
 *     percentage; the peak for bytes per second, with the peak printed in the header, because an
 *     autoscaled axis with no number on it is a shape rather than a measurement.
 *
 * Hovering shows the readings under the pointer - the time, then each series' value - through
 * Webwin's own tooltip, with a guide line and a dot on each series at that sample.
 */
import {
  Control,
  type Color,
  type ControlOptions,
  type Point,
  type Rect,
  type RenderContext,
  type Size,
  type Theme,
  type TooltipContent
} from 'webwin';
import { crm } from '../theme/CrmTheme.js';

/** One line. `points` are in the series' own units, oldest first. */
export interface Series {
  readonly label: string;
  readonly points: readonly number[];
  /**
   * When each point was captured, in epoch milliseconds, same length as `points`.
   *
   * Optional, and it changes what the X axis MEANS. Without it the points are evenly spaced, which
   * is right for a series that is one reading per fixed interval; with it a point lands where its own
   * timestamp falls inside the window, so a device that was offline for ten minutes draws a stretched
   * segment across the gap rather than quietly compressing it away. The device page passes it
   * (samples arrive at 2s or 10s depending on whether anyone is watching, and stop altogether when
   * the machine sleeps); a series with a guaranteed cadence need not.
   */
  readonly at?: readonly number[];
  /** Index into the chart palette. Kept per series so a legend chip and its line always agree. */
  readonly colorIndex: number;
}

export interface SparklineOptions extends ControlOptions {
  /** The ceiling. Null autoscales to the window's own peak. */
  max?: number | null;
  /** The most points drawn. More than this is downsampled by bucket maximum. */
  maxPoints?: number;
  /** How a reading reads in the hover tooltip ("6%", "33.5 °C", "1.2 MB/s"). */
  format?: (value: number) => string;
}

/** The sample the pointer is over: where the guide line goes, and where each series' dot sits. */
interface Hover {
  readonly x: number;
  readonly dots: readonly { readonly colorIndex: number; readonly y: number }[];
}

/** The frame paint and hover share, so the guide line lands exactly on the line it reads. */
interface Geometry {
  readonly r: Rect;
  readonly from: number;
  readonly span: number;
  readonly ceiling: number;
}

/**
 * The chart palette, in order.
 *
 * Drawn from the CRM palette rather than invented, so a series line and its legend chip can never
 * disagree and a theme switch moves both. Six is enough for temperatures, the widest family here.
 */
const PALETTE_KEYS = ['brand', 'ok', 'warn', 'danger', 'info', 'brandDark'] as const;

/**
 * The colour of series `index`.
 *
 * Exported so a legend swatch is painted from the same table as the line it labels - the pair
 * disagreeing is the one defect a legend can have that is worse than having no legend.
 */
export function chartColor(theme: Theme, index: number): Color {
  const c = crm(theme);
  return c[PALETTE_KEYS[index % PALETTE_KEYS.length]!];
}

export class Sparkline extends Control {
  #series: readonly Series[] = [];
  #hover: Hover | null = null;
  max: number | null;
  maxPoints: number;
  format: (value: number) => string;

  constructor(options: SparklineOptions = {}) {
    super({ height: 48, hAlign: 'stretch', ...options });
    this.max = options.max ?? null;
    this.maxPoints = options.maxPoints ?? 160;
    this.format = options.format ?? (v => (Math.round(v * 10) / 10).toString());
    this.on('pointerleave', () => this.#setHover(null));
  }

  /** Stretches wide and keeps its own height: a chart's height is a design decision, not content. */
  override preferredSize(available: Size): Size {
    return { width: available.width, height: this.height };
  }

  get series(): readonly Series[] {
    return this.#series;
  }

  set series(value: readonly Series[]) {
    this.#series = value.map(s => {
      if (!s.at) return { ...s, points: downsample(s.points, this.maxPoints) };
      // Both halves are bucketed together, or a downsampled series would plot its peaks at the
      // wrong times: the value is the bucket's maximum and the timestamp is the bucket's last, which
      // is the pair the OpenSilver builder used.
      const [points, at] = downsamplePairs(s.points, s.at, this.maxPoints);
      return { ...s, points, at };
    });
    this.invalidate();
  }

  /** The peak across every series, for a header that has to name an autoscaled axis. */
  peak(): number {
    let peak = 0;
    for (const s of this.#series) for (const p of s.points) peak = Math.max(peak, p);
    return peak;
  }

  /**
   * Hit-testable so it can answer `getTooltip`. Safe on a scrolling page: Webwin bubbles the wheel
   * up the parent chain, and a one-finger touch slide asks each ancestor in turn to pan, so the page
   * under a chart still scrolls either way.
   */
  protected override hitTestSelf(): boolean {
    return true;
  }

  /**
   * The readings under the pointer: the time, then each series' value at that moment.
   *
   * Webwin's tooltip manager asks on every pointer move, which is also when the guide line has to
   * follow - so the hover state is set here rather than from a second pointer handler repeating the
   * same nearest-sample search.
   */
  override getTooltip(p: Point): TooltipContent | null {
    const g = this.#geometry();
    if (!g) return this.#setHover(null);
    const fraction = Math.max(0, Math.min(1, (p.x - g.r.x) / g.r.width));
    const time = g.span > 0 ? g.from + fraction * g.span : null;

    const lines: string[] = [];
    const dots: { colorIndex: number; y: number }[] = [];
    let guideX: number | null = null;
    let when: number | null = null;
    for (const s of this.#series) {
      if (s.points.length === 0) continue;
      const index = nearestIndex(s, fraction, time);
      const at = s.at?.[index];
      // A sensor that was not reporting at that moment has no reading there to offer.
      if (time !== null && at !== undefined && Math.abs(at - time) > g.span / 20) continue;
      const reading = s.points[index]!;
      guideX ??= xFor(g, s, index);
      when ??= at ?? null;
      dots.push({ colorIndex: s.colorIndex, y: yFor(g.r, reading, g.ceiling) });
      lines.push(this.#series.length > 1 ? `${s.label}: ${this.format(reading)}` : this.format(reading));
    }
    if (lines.length === 0) return this.#setHover(null);

    this.#setHover({ x: guideX ?? p.x, dots });
    return (when !== null ? [timeLabel(when, g.span), ...lines] : lines).join('\n');
  }

  #setHover(next: Hover | null): null {
    const changed =
      (next === null) !== (this.#hover === null) ||
      (next !== null && this.#hover !== null && (next.x !== this.#hover.x || next.dots.length !== this.#hover.dots.length));
    this.#hover = next;
    if (changed) this.invalidate();
    return null;
  }

  #geometry(): Geometry | null {
    const ceiling = this.max ?? this.peak();
    if (ceiling <= 0) return null;
    // One window for every series, taken from the widest span any of them covers - so two series on
    // one chart share an axis rather than each being stretched to its own extent.
    let from = Number.POSITIVE_INFINITY;
    let to = Number.NEGATIVE_INFINITY;
    for (const s of this.#series) {
      for (const t of s.at ?? []) {
        from = Math.min(from, t);
        to = Math.max(to, t);
      }
    }
    return { r: this.localRect, from, span: to > from ? to - from : 0, ceiling };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const r = this.localRect;

    rc.drawRect(r, { fill: c.altSurface, radius: 4 });

    // A single baseline rather than a grid: at 48px tall a grid is noise, and the one line worth
    // having is the zero the eye measures the shape against.
    rc.fillRect({ x: r.x, y: r.y + r.height - 1, width: r.width, height: 1 }, c.border);

    const g = this.#geometry();
    if (!g) return;

    for (const s of this.#series) {
      if (s.points.length < 2) continue;
      const points = s.points.map((reading, i) => ({ x: xFor(g, s, i), y: yFor(r, reading, g.ceiling) }));
      rc.drawPolyline(points, 1.5, chartColor(theme, s.colorIndex));
    }

    const hover = this.#hover;
    if (hover) {
      rc.fillRect({ x: Math.round(hover.x), y: r.y, width: 1, height: r.height }, c.mutedText);
      for (const dot of hover.dots) {
        rc.drawRect(
          { x: hover.x - 3, y: dot.y - 3, width: 6, height: 6 },
          { fill: chartColor(theme, dot.colorIndex), radius: 3 }
        );
      }
    }
  }
}

function xFor(g: Geometry, s: Series, index: number): number {
  if (s.at && g.span > 0) return g.r.x + ((s.at[index]! - g.from) / g.span) * g.r.width;
  return s.points.length < 2 ? g.r.x : g.r.x + (index * g.r.width) / (s.points.length - 1);
}

/**
 * A reading's height in the chart.
 *
 * Clamped rather than clipped: a reading past a FIXED ceiling (a CPU counter reporting 101) should
 * draw at the top, not off the top, where it would read as a gap in the data.
 */
function yFor(r: Rect, reading: number, ceiling: number): number {
  return r.y + r.height - Math.max(0, Math.min(1, reading / ceiling)) * (r.height - 2) - 1;
}

/** The sample nearest the pointer: by timestamp where the series has them, by position otherwise. */
function nearestIndex(s: Series, fraction: number, time: number | null): number {
  if (s.at && time !== null) {
    let best = 0;
    for (let i = 1; i < s.at.length; i++) {
      if (Math.abs(s.at[i]! - time) < Math.abs(s.at[best]! - time)) best = i;
    }
    return best;
  }
  return Math.round(fraction * (s.points.length - 1));
}

/** "14:32:05" across the live half hour; "Mon 14:32" once the window spans more than half a day. */
function timeLabel(at: number, span: number): string {
  const date = new Date(at);
  return span > 12 * 60 * 60 * 1000
    ? date.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Keeps at most `limit` points, taking the MAXIMUM of each bucket.
 *
 * The rule the 24-hour view turns on. Averaging a 60-sample bucket down to one point loses exactly
 * the one-minute spike somebody opened the chart to find.
 */
export function downsample(points: readonly number[], limit: number): number[] {
  return downsampleWith(points, limit, (source, from, to) => {
    let peak = source[from]!;
    for (let j = from + 1; j < to; j++) peak = Math.max(peak, source[j]!);
    return peak;
  });
}

/** `downsample` for a value/time pair: the bucket's maximum, at the bucket's last timestamp. */
export function downsamplePairs(
  points: readonly number[],
  at: readonly number[],
  limit: number
): [points: number[], at: number[]] {
  return [
    downsample(points, limit),
    downsampleWith(at, limit, (source, _from, to) => source[to - 1]!)
  ];
}

function downsampleWith(
  points: readonly number[],
  limit: number,
  pick: (source: readonly number[], from: number, to: number) => number
): number[] {
  if (points.length <= limit) return [...points];
  const size = points.length / limit;
  const out: number[] = [];
  for (let i = 0; i < limit; i++) {
    const from = Math.floor(i * size);
    const to = Math.min(points.length, Math.max(from + 1, Math.floor((i + 1) * size)));
    out.push(pick(points, from, to));
  }
  return out;
}
